<template>
  <div class="user-plugins-container">
    <!-- 搜索区域 -->
    <div class="search-bar">
      <el-row :gutter="20" align="middle">
        <el-col :span="18">
          <el-space>
            <!-- 搜索框 -->
            <el-input
                v-model="searchKeyword"
                placeholder="输入插件名称搜索"
                style="width: 300px"
                clearable
                :prefix-icon="Search"
                @clear="clearSearch"
                @keyup.enter="searchProducts"
            />
            <el-button type="primary" @click="searchProducts">搜索</el-button>
          </el-space>
        </el-col>
      </el-row>
    </div>

    <div class="table-wrapper">
      <el-table
        :data="products"
        v-loading="loading"
        class="product-table"
        stripe
        border
        max-height="600"
        empty-text="暂无相关信息"
      >
        <el-table-column prop="name" label="插件名称" min-width="140" align="center" />
        <el-table-column prop="productCode" label="插件编码" min-width="130" align="center" />

        <el-table-column prop="version" label="使用版本" width="140" align="center">
          <template #default="scope">
            <el-tag size="small" effect="plain" class="version-tag">
              {{ scope.row.version }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="description" label="描述" min-width="220" align="center" style="background: darkorange">
          <template #default="scope">
            <div class="description-cell">
              {{ scope.row.description || '暂无描述' }}
            </div>
          </template>
        </el-table-column>

        <el-table-column label="有效期" width="200" align="center">
          <template #default="scope">
            <div v-if="scope.row.isPublic">
              <!-- 公共插件：显示状态徽章 + 详情按钮 -->
              <el-tag :type="getStatusType(scope.row.productExpireTime)" size="small">
                {{ getStatusText(scope.row.productExpireTime) }}
              </el-tag>
              <el-button size="small" text type="primary" @click="showActivationDetails(scope.row)">
                详情
              </el-button>
            </div>
            <div v-else>
              <!-- 非公共插件：仍显示到期时间 -->
              <span :class="getExpireTimeClass(scope.row.productExpireTime)">
                {{ formatTime(scope.row.productExpireTime) }}
                {{ getExpireTimeStatus(scope.row.productExpireTime) }}
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="130" align="center" fixed="right">
          <template #default="scope">
            <div class="action-buttons">
              <el-button
                  size="small"
                  @click="openRenewDialog(scope.row)"
                  :disabled="isLoadingRenewal"
                  class="action-btn"
                  :class="{
                    'expired-btn': isExpired(scope.row.productExpireTime),
                    'active-btn': !isExpired(scope.row.productExpireTime)
                  }"
              >
                续费
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          :pager-count="5"
          layout="prev, pager, next"
          @current-change="handleCurrentChange"
      />
    </div>

    <!-- 续费弹窗 -->
    <el-dialog
        v-model="renewDialogVisible"
        width="900px"
        :before-close="closeRenewDialog"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
        class="renew-dialog"
        modal-class="renew-dialog-overlay"
    >

      <template #header>
        <div class="custom-header">
          <span></span>
          <button class="custom-close-btn" @click="closeRenewDialog">✕</button>
        </div>
      </template>

      <div class="renew-dialog-content" v-if="currentPlugin">
        <!-- 插件基本信息 -->
        <div class="plugin-info-section">
          <div class="plugin-info-header">
            <h4>{{ currentPlugin.name }}</h4>
            <div class="current-expire">
              当前到期:
              <span :class="getExpireTimeClass(currentPlugin.productExpireTime)">
            {{ formatTime(currentPlugin.productExpireTime) }}
            {{ getExpireTimeStatus(currentPlugin.productExpireTime) }}
          </span>
            </div>
          </div>
          <div class="plugin-description">
            {{ currentPlugin.description || '暂无描述' }}
          </div>
        </div>

        <div class="renew-main-content">
          <!-- 左侧：周期选择 -->
          <div class="period-selection-section">
<!--            <div class="section-title">-->
<!--              <el-icon><Clock /></el-icon>-->
<!--              <span>选择续费周期</span>-->
<!--            </div>-->

            <!-- 一行显示的周期选择 -->
            <div class="period-horizontal-container">
              <div
                  v-for="period in renewPeriodOptions"
                  :key="period.value"
                  class="period-horizontal-option"
                  :class="{
                    'active': selectedRenewPeriod === period.value,
                    'popular': period.value === '90',
                    'premium': period.value === '365'
                  }"
                  @click="selectRenewPeriod(period.value)"
              >
                <div class="period-top-badge" v-if="period.badge">
                  {{ period.badge }}
                </div>
                <div class="period-main-content">
                  <div class="period-label">{{ period.label }}</div>
                  <div class="period-price">
                    <span class="currency">¥</span>
                    <span class="amount">{{ period.price }}</span>
                  </div>
                  <div class="period-days">{{ period.days }}天</div>
                  <div class="period-desc" v-if="period.desc">{{ period.desc }}</div>
                </div>
                <div class="period-check-mark" v-if="selectedRenewPeriod === period.value">
                  <el-icon><Check /></el-icon>
                </div>
              </div>
            </div>

            <!-- 续费提示信息 -->
            <div class="renew-tips">
              <div class="tips-header">
                <el-icon><InfoFilled /></el-icon>
                <span>续费说明</span>
              </div>
              <div class="tips-content">
                <p> 续费将从当前到期时间开始计算延长期限</p>
                <p> 支付成功后，系统将自动更新到期时间</p>
                <p> 支持微信支付，扫码完成支付</p>
                <p> 如有问题，请联系客服：18925330037</p>
              </div>
            </div>
          </div>

          <!-- 右侧：支付区域 -->
          <div class="payment-section">
            <!--            <div class="section-title">-->
            <!--              <el-icon><CreditCard /></el-icon>-->
            <!--              <span>支付信息</span>-->
            <!--            </div>-->

            <!-- 支付金额 -->
            <div class="payment-amount">
              <div class="amount-label">支付金额</div>
              <div class="amount-value">
                ¥{{ renewPeriodOptions.find(p => p.value === selectedRenewPeriod)?.price || 0 }}
              </div>
            </div>

            <!-- 二维码区域 -->
            <div class="qrcode-container">
              <div class="qrcode-header">
                <el-icon>
                  <svg t="1761617723278" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4874" width="28" height="28">
                    <path d="M1024 619.52c0-143.36-138.24-256-307.2-256s-307.2 112.64-307.2 256 138.24 256 307.2 256c30.72 0 61.44-5.12 92.16-10.24l97.28 51.2-25.6-76.8c87.04-51.2 143.36-128 143.36-220.16z m-414.72-40.96c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z m209.92 0c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z" fill="#4CBF00" p-id="4875"></path>
                    <path d="M358.4 609.28c0-158.72 153.6-286.72 348.16-286.72h15.36c-40.96-133.12-179.2-235.52-353.28-235.52-204.8 0-368.64 138.24-368.64 307.2 0 107.52 66.56 204.8 168.96 256l-30.72 92.16L256 686.08c35.84 10.24 71.68 15.36 112.64 15.36h10.24c-15.36-30.72-20.48-61.44-20.48-92.16z m138.24-414.72c35.84 0 66.56 30.72 66.56 66.56s-30.72 66.56-66.56 66.56C460.8 322.56 430.08 291.84 430.08 256S460.8 194.56 496.64 194.56zM245.76 322.56c-35.84 0-61.44-30.72-61.44-66.56s30.72-66.56 66.56-66.56 61.44 30.72 61.44 66.56-30.72 66.56-66.56 66.56z" fill="#4CBF00" p-id="4876"></path>
                  </svg>
                </el-icon>
                微信扫码支付
              </div>

              <div class="qrcode-wrapper">
                <!-- 二维码图片 -->
                <img
                    v-show="payQrCode"
                    :src="payQrCode"
                    alt="微信支付二维码"
                    class="qrcode-image"
                />

                <!-- 加载占位 -->
                <div v-show="!payQrCode" class="qrcode-loading-placeholder">
                  <el-icon class="loading-icon"><Loading /></el-icon>
                  <span></span>
                </div>

                <!-- 支付成功遮罩 -->
                <div v-if="paymentStatus === 'success'" class="success-overlay">
                  <div class="success-content">
                    <el-icon color="#52c41a" :size="60"><Check /></el-icon>
                    <p class="success-text">支付成功</p>
                  </div>
                </div>

                <!-- 二维码过期遮罩 -->
                <div
                    v-if="isQrCodeExpired && paymentStatus === 'pending'"
                    class="expired-overlay"
                    @click="refreshQrCode"
                >
                  <div class="expired-content">
                    <el-icon color="#f56c6c" :size="30"><Refresh /></el-icon>
                    <p class="expired-text">二维码已过期</p>
                  </div>
                </div>
              </div>

              <!-- 支付状态提示 -->
<!--              <div class="payment-status-hint" v-if="payQrCode">-->
<!--                <div v-if="paymentStatus === 'success'" class="status-success">-->
<!--                  <el-icon><CircleCheck /></el-icon>-->
<!--                  <span>支付成功！3秒后自动关闭</span>-->
<!--                </div>-->
<!--              </div>-->
            </div>

          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 激活记录弹窗 -->
    <el-dialog
        v-model="activationDialogVisible"
        width="820px"
        destroy-on-close
        :close-on-click-modal="false"
        :close-on-press-escape="false"
        :show-close="false"
        class="registration-code-history-dialog"
        modal-class="registration-code-history-overlay"
    >
      <!-- 自定义头部 -->
      <template #header>
        <div class="registration-code-history-header">
          <span class="dialog-title">
            {{ currentPluginForDetails?.name }} - 激活记录
          </span>
          <div class="header-actions">
            <el-tag type="success" size="small">有效：{{ validCount }}</el-tag>
            <el-tag type="info" size="small">全部：{{ activationDetails.length }}</el-tag>
            <button class="registration-code-history-custom-close-btn" @click="activationDialogVisible = false">✕</button>
          </div>
        </div>
      </template>

      <!-- 表格区域 -->
      <div class="registration-code-history-table-wrapper">
        <el-table
            :data="activationDetails"
            v-loading="detailsLoading"
            border
            stripe
            class="registration-code-history-table"
            max-height="450"
        >
          <el-table-column prop="deviceFingerprint" label="注册码" min-width="200" align="center">
            <template #default="scope">
              <span class="mono-text">{{ scope.row.deviceFingerprint || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="激活码" min-width="220" align="center">
            <template #default="scope">
              <span class="mono-text">{{ maskActivationCode(scope.row.activationCode) }}</span>
              <el-button size="small" text type="primary" @click="copyCode(scope.row.activationCode)">
                复制
              </el-button>
            </template>
          </el-table-column>

          <el-table-column label="状态" width="100" align="center">
            <template #default="scope">
              <el-tag :type="scope.row.isValid ? 'success' : 'danger'" size="small">
                {{ scope.row.isValid ? '有效' : '已过期' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="到期时间" width="180" align="center">
            <template #default="scope">
              <span :class="{
                'normal': scope.row.isValid && getDaysLeft(scope.row.activationExpireTime) > 7,
                'expiring': scope.row.isValid && getDaysLeft(scope.row.activationExpireTime) <= 7,
                'expired': !scope.row.isValid
              }">
                {{ formatTime(scope.row.activationExpireTime) }}
                <span v-if="!scope.row.isValid">(已过期)</span>
                <span v-else-if="getDaysLeft(scope.row.activationExpireTime) <= 7">(临期)</span>
              </span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="details-pagination" style="margin-top: 16px; display: flex; justify-content: center;">
        <el-pagination
            v-model:current-page="detailsCurrentPage"
            :page-size="detailsPageSize"
            :total="detailsTotal"
            layout="prev, pager, next"
            @current-change="handleDetailsPageChange"
        />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import { Close, Search } from '@element-plus/icons-vue'
import request from '@/utils/request'
import QRCode from 'qrcode'

// 响应式数据
const products = ref([])
const loading = ref(false)
const searchKeyword = ref('')
const searchLoading = ref(false)

// 续费相关变量
const renewDialogVisible = ref(false)
const currentPlugin = ref(null)
const selectedRenewPeriod = ref('30') // 默认选择一个月
const renewLoading = ref(false)
const payQrCode = ref('')
const paymentStatus = ref('pending')
const renewPollInterval = ref(null)
const currentRenewOrderNo = ref('')
const isLoadingRenewal = ref(false)

const qrCodeGeneratedTime = ref(0) // 二维码生成时间戳
const isQrCodeExpired = ref(false) // 二维码是否过期
const qrCodeExpireCheckInterval = ref(null) // 过期检查定时器
const QR_CODE_EXPIRE_TIME = 5 * 60 * 1000 // 5分钟过期时间（毫秒）

const isCurrentPublic = ref(false)  // 当前插件是否为公共插件

// 激活码明细弹窗
const activationDialogVisible = ref(false)
const currentPluginForDetails = ref(null)
const activationDetails = ref([])
const detailsLoading = ref(false)

// 激活码明细弹窗分页
const detailsCurrentPage = ref(1)
const detailsPageSize = ref(5)
const detailsTotal = ref(0)

// 获取状态类型（用于徽章）
const getStatusType = (expireTime) => {
  if (!expireTime) return 'danger'
  const diff = new Date(expireTime).getTime() - Date.now()
  if (diff < 0) return 'danger'
  if (diff < 7 * 24 * 60 * 60 * 1000) return 'warning'
  return 'success'
}

// 获取状态文本
const getStatusText = (expireTime) => {
  if (!expireTime) return '已过期'
  const diff = new Date(expireTime).getTime() - Date.now()
  if (diff < 0) return '已过期'
  if (diff < 7 * 24 * 60 * 60 * 1000) return '即将过期'
  return '有效'
}

// 计算有效数量
const validCount = computed(() => {
  return activationDetails.value.filter(item => item.isValid).length
})

// 存储插件各周期价格
const pluginPrices = ref({
  price30Days: 0,
  price90Days: 0,
  price365Days: 0
})

// 续费周期选项
const renewPeriodOptions = computed(() => {
  if (isCurrentPublic.value && currentPlugin.value) {
    // 公共插件：仅一年期，价格取产品表 price
    return [
      {
        label: '一年',
        value: '365',
        days: 365,
        price: (currentPlugin.value.price || 0).toFixed(2),
        badge: '固定年费',
        desc: '一年有效期'
      }
    ]
  } else {
    // 非公共插件：原有三个选项（30/90/365天）
    const prices = pluginPrices.value
    return [
      {
        label: '一个月',
        value: '30',
        days: 30,
        price: (prices.price30Days || 0).toFixed(2),
        badge: '基础',
        desc: '短期灵活'
      },
      {
        label: '一个季度',
        value: '90',
        days: 90,
        price: (prices.price90Days || 0).toFixed(2),
        badge: '推荐',
        desc: '性价比高'
      },
      {
        label: '一年',
        value: '365',
        days: 365,
        price: (prices.price365Days || 0).toFixed(2),
        badge: '超值',
        desc: '最优惠'
      }
    ]
  }
})


// 分页相关变量
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)

// 获取插件列表
const fetchProducts = async () => {
  loading.value = true
  try {
    const response = await request.get('/plugin/all/products', {
      params: {
        page: currentPage.value,
        size: pageSize.value
      }
    })
    if (response.code === '200') {
      products.value = response.data.list || []
      total.value = response.data.total

    } else {
      // ElMessage.error(response.message || '获取插件信息失败')
    }
  } catch (error) {
    console.error('获取插件信息失败:', error)
    // ElMessage.error('获取插件信息失败: ' + (error.message || '网络错误'))
  } finally {
    loading.value = false
  }
}

// 清空搜索
const clearSearch = () => {
  searchKeyword.value = ''
  fetchProducts()
}

// 搜索插件
const searchProducts = async () => {
  if (!searchKeyword.value.trim()) {
    fetchProducts()
    return
  }

  searchLoading.value = true
  try {
    const response = await request.get('/plugin/search/products', {
      params: {
        keyword: searchKeyword.value,
        page: currentPage.value,
        size: pageSize.value
      }
    })
    if (response.code === '200') {
      products.value = response.data.list || []
      total.value = response.data.total

      if (products.value.length === 0) {
        ElMessage.info('未找到匹配的产品')
      }
    } else {
      // ElMessage.error(response.message || '搜索插件失败')
    }
  } catch (error) {
    console.error('搜索插件失败:', error)
    // ElMessage.error('搜索插件失败: ' + (error.message || '网络错误'))
  } finally {
    searchLoading.value = false
  }
}

// 分页变化处理
const handleCurrentChange = (page) => {
  currentPage.value = page
  if (searchKeyword.value) {
    searchProducts()
  } else {
    fetchProducts()
  }
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  const date = new Date(time)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 获取到期时间状态
const getExpireTimeStatus = (expireTime) => {
  if (!expireTime) return ''

  const now = new Date()
  const expireDate = new Date(expireTime)
  const diffTime = expireDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays < 0) {
    return '(已过期)'
  } else if (diffDays <= 7) {
    return '(临期)'
  }
  return ''
}

// 获取到期时间样式类
const getExpireTimeClass = (expireTime) => {
  if (!expireTime) return ''

  const now = new Date()
  const expireDate = new Date(expireTime)
  const diffTime = expireDate.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays < 0) {
    return 'expired'
  } else if (diffDays <= 7) {
    return 'expiring'
  } else {
    return 'normal'
  }
}

// 检查是否已过期
const isExpired = (expireTime) => {
  if (!expireTime) return true
  const now = new Date()
  const expireDate = new Date(expireTime)
  return expireDate.getTime() < now.getTime()
}

const selectRenewPeriod = (value) => {
  selectedRenewPeriod.value = value
  onRenewPeriodChange()
}

// 根据产品编码和版本号获取产品ID
const getProductIdByCodeAndVersion = async (productCode, version) => {
  try {
    const response = await request.get('/plugin/getProductId', {
      params: {
        productCode: productCode,
        version: version
      }
    });

    if (response.code === '200') {
      // console.log(response.data)
      return response.data; // 返回产品ID
    } else {
      console.error('获取产品ID失败:', response.message);
      return null;
    }
  } catch (error) {
    console.error('获取产品ID失败:', error);
    return null;
  }
};

// 打开续费弹窗
const openRenewDialog = async (plugin) => {
  // 1. 重置支付状态（和您原有顺序一致）
  resetRenewPaymentState()

  // 2. 获取产品ID
  const productId = await getProductIdByCodeAndVersion(
      plugin.productCode,
      plugin.version
  );
  if (!productId) {
    ElMessage.error('无法获取插件ID，请稍后重试');
    return;
  }

  // 3. 保存插件数据，并标记是否公共插件
  currentPlugin.value = {
    ...plugin,
    productId: productId,
    isPublic: plugin.isPublic || false   // ← 新增：标记公共插件
  };
  isCurrentPublic.value = plugin.isPublic || false  // ← 新增
  renewDialogVisible.value = true

  // 4. 获取价格（公共插件会走特殊逻辑）
  await fetchPluginPrice(productId)

  // 5. 根据类型选择默认周期（公共→一年，非公共→一个月）
  selectedRenewPeriod.value = isCurrentPublic.value ? '365' : '30'  // ← 改动

  // 6-8. 清空旧支付状态（与您原有相同）
  payQrCode.value = ''
  paymentStatus.value = 'pending'
  currentRenewOrderNo.value = ''

  // 9. 延迟生成二维码
  setTimeout(() => {
    generateRenewQrCode()
  }, 300)
}

// 获取插件价格
// const fetchPluginPrice = async (productId) => {
//   try {
//     console.log(productId)
//     const response = await request.get(`/plugin/details/${productId}`)
//     if (response.code === '200' && response.data) {
//       pluginBasePrice.value = response.data.price
//
//     } else {
//       console.error('获取插件价格失败:', response.message)
//
//     }
//   } catch (error) {
//     console.error('获取插件价格失败:', error)
//
//   }
// }
const fetchPluginPrice = async (productId) => {
  if (isCurrentPublic.value && currentPlugin.value) {
    // 公共插件：价格来自产品表
    pluginPrices.value = {
      price30Days: 0,
      price90Days: 0,
      price365Days: currentPlugin.value.price || 0
    }
    return
  }

  // 非公共插件：原有请求逻辑
  try {
    const response = await request.get(`/plugin/details-with-pricing/${productId}`)
    if (response.code === '200' && response.data) {
      pluginPrices.value = {
        price30Days: response.data.price30Days || 0,
        price90Days: response.data.price90Days || 0,
        price365Days: response.data.price365Days || 0
      }
    }
  } catch (error) {
    console.error('获取插件价格失败:', error)
  }
}

// 重置续费支付状态
const resetRenewPaymentState = () => {
  // 清除所有定时器
  if (renewPollInterval.value) {
    clearInterval(renewPollInterval.value)
    renewPollInterval.value = null
  }

  if (qrCodeExpireCheckInterval.value) {
    clearInterval(qrCodeExpireCheckInterval.value)
    qrCodeExpireCheckInterval.value = null
  }

  // 重置支付状态
  payQrCode.value = ''
  paymentStatus.value = 'pending'
  renewLoading.value = false

  // 重置过期相关状态
  isQrCodeExpired.value = false
  qrCodeGeneratedTime.value = 0
}

// 完全重置续费弹窗状态
const resetRenewDialogState = () => {
  // 重置插件相关
  currentPlugin.value = null
  pluginPrices.value = {
    price30Days: 0,
    price90Days: 0,
    price365Days: 0
  }

  // 重置选择
  selectedRenewPeriod.value = '30'

  // 重置支付相关
  payQrCode.value = ''
  paymentStatus.value = 'pending'
  currentRenewOrderNo.value = ''
  renewLoading.value = false

  // 重置过期相关
  isQrCodeExpired.value = false
  qrCodeGeneratedTime.value = 0

  // 清除所有定时器
  if (renewPollInterval.value) {
    clearInterval(renewPollInterval.value)
    renewPollInterval.value = null
  }

  if (qrCodeExpireCheckInterval.value) {
    clearInterval(qrCodeExpireCheckInterval.value)
    qrCodeExpireCheckInterval.value = null
  }
}

// 生成续费支付二维码
const generateRenewQrCode = async () => {
  // 如果已有未支付的订单，先关闭它
  if (currentRenewOrderNo.value && paymentStatus.value === 'pending') {
    await closeRenewOrder(currentRenewOrderNo.value)
    console.log('已关闭旧订单:', currentRenewOrderNo.value)
  }

  // 如果二维码已过期，先清理旧订单状态
  if (isQrCodeExpired.value && currentRenewOrderNo.value) {
    // 确保旧订单已关闭
    await closeRenewOrder(currentRenewOrderNo.value)
  }

  // 重置所有状态，开始新的支付流程
  resetRenewPaymentState()

  // 添加验证，确保当前有插件数据
  if (!currentPlugin.value) {
    ElMessage.error('插件信息加载失败，请重试')
    return
  }

  // 确保插件价格已加载
  if (pluginPrices.value.price30Days === 0 &&
      pluginPrices.value.price90Days === 0 &&
      pluginPrices.value.price365Days === 0) {
    ElMessage.warning('正在加载插件价格，请稍候...')
    // 重新获取价格
    await fetchPluginPrice(currentPlugin.value.productId)
  }

  renewLoading.value = true
  paymentStatus.value = 'pending'
  isQrCodeExpired.value = false // 重置过期状态
  qrCodeGeneratedTime.value = Date.now() // 记录生成时间
  // 注意：这里不需要重置 currentRenewOrderNo.value，closeRenewOrder 已经处理

  try {
    const selectedPeriod = renewPeriodOptions.value.find(
        opt => opt.value === selectedRenewPeriod.value
    )

    // 验证参数
    if (!selectedPeriod) {
      ElMessage.error('请选择续费周期')
      return
    }

    const response = await request.post('/pay/createQrCode', {
      productId: currentPlugin.value.productId || currentPlugin.value.id,
      productName: currentPlugin.value.name,
      days: selectedPeriod.days,
      amount: selectedPeriod.price, // 使用价格表中的价格
      description: `续费 ${currentPlugin.value.name} ${selectedPeriod.label}使用权`,
      paymentType: 'wechat_native',
      isRenewal: true,
      originalExpireTime: currentPlugin.value.productExpireTime
    })

    if (response.code === '200' && response.data.success) {
      // 生成二维码图片
      const qrCodeDataURL = await QRCode.toDataURL(response.data.qrCodeUrl, {
        width: 220,
        margin: 2,
        color: {
          dark: '#1a1a1a',
          light: '#ffffff'
        }
      })
      payQrCode.value = qrCodeDataURL
      currentRenewOrderNo.value = response.data.outTradeNo

      // 清除旧的轮询和过期检查
      if (renewPollInterval.value) {
        clearInterval(renewPollInterval.value)
      }
      if (qrCodeExpireCheckInterval.value) {
        clearInterval(qrCodeExpireCheckInterval.value)
      }

      // 启动新的轮询
      startRenewPolling(response.data.outTradeNo)

      // 启动二维码过期检查
      startQrCodeExpireCheck()

    } else {
      // 错误处理
    }
  } catch (error) {
    console.error('生成支付二维码失败:', error)
  } finally {
    renewLoading.value = false
  }
}

// 轮询支付状态
const startRenewPolling = (outTradeNo) => {
  if (renewPollInterval.value) {
    clearInterval(renewPollInterval.value)
  }

  renewPollInterval.value = setInterval(async () => {
    try {
      const response = await request.get(`/pay/pollStatus/${outTradeNo}`)

      if (response.code === '200' && response.data.success) {
        if (response.data.paid) {
          // 支付成功
          paymentStatus.value = 'success'
          clearInterval(renewPollInterval.value)
          renewPollInterval.value = null

          // 停止过期检查
          if (qrCodeExpireCheckInterval.value) {
            clearInterval(qrCodeExpireCheckInterval.value)
            qrCodeExpireCheckInterval.value = null
          }

          ElMessage.success({
            message: '续费成功！插件到期时间已更新',
            duration: 3000
          })

          // 刷新插件列表
          setTimeout(() => {
            fetchProducts()
          }, 1000)
        }
      }
    } catch (error) {
      console.error('轮询支付状态失败:', error)
    }
  }, 3000)
}

// 续费周期改变事件
const onRenewPeriodChange = () => {
  generateRenewQrCode()
}

// 启动二维码过期检查
const startQrCodeExpireCheck = () => {
  if (qrCodeExpireCheckInterval.value) {
    clearInterval(qrCodeExpireCheckInterval.value)
  }

  qrCodeExpireCheckInterval.value = setInterval(() => {
    const now = Date.now()
    const elapsedTime = now - qrCodeGeneratedTime.value

    if (elapsedTime >= QR_CODE_EXPIRE_TIME && paymentStatus.value === 'pending') {
      // 二维码过期时执行的操作
      isQrCodeExpired.value = true
      clearInterval(qrCodeExpireCheckInterval.value)
      qrCodeExpireCheckInterval.value = null

      // 1. 停止支付状态轮询
      if (renewPollInterval.value) {
        clearInterval(renewPollInterval.value)
        renewPollInterval.value = null
      }

      // 2. 关闭当前订单
      if (currentRenewOrderNo.value) {
        closeRenewOrder(currentRenewOrderNo.value)
        ElMessage.warning('二维码已过期，订单已自动关闭，请刷新二维码重新支付')
      }
    }
  }, 1000) // 每秒检查一次
}

// 刷新二维码（过期时点击遮盖层调用）
const refreshQrCode = async () => {
  if (isQrCodeExpired.value) {
    // 先关闭旧订单（如果存在且未支付）
    if (currentRenewOrderNo.value && paymentStatus.value === 'pending') {
      await closeRenewOrder(currentRenewOrderNo.value)
    }

    // 重置过期状态
    isQrCodeExpired.value = false

    // 生成新二维码
    generateRenewQrCode()
  }
}

// 关闭续费弹窗
const closeRenewDialog = () => {
  renewDialogVisible.value = false

  // 清除所有定时器
  if (renewPollInterval.value) {
    clearInterval(renewPollInterval.value)
    renewPollInterval.value = null
  }

  if (qrCodeExpireCheckInterval.value) {
    clearInterval(qrCodeExpireCheckInterval.value)
    qrCodeExpireCheckInterval.value = null
  }

  // 关闭弹窗时清理订单
  if (currentRenewOrderNo.value && paymentStatus.value === 'pending') {
    closeRenewOrder(currentRenewOrderNo.value)
  }

  // 完全重置所有状态
  resetRenewDialogState()
  isQrCodeExpired.value = false // 重置过期状态
  qrCodeGeneratedTime.value = 0 // 重置生成时间
}

// 关闭订单
const closeRenewOrder = async (outTradeNo) => {
  if (!outTradeNo) return

  try {
    await request.post(`/pay/closeOrder/${outTradeNo}`)
  } catch (error) {
    console.error('关闭订单失败:', error)
  }
}

// 手动检查支付状态
const manualCheckPayment = async () => {
  if (!currentRenewOrderNo.value) return

  try {
    const response = await request.get(`/pay/pollStatus/${currentRenewOrderNo.value}`)
    if (response.code === '200' && response.data.success && response.data.paid) {
      paymentStatus.value = 'success'
      if (renewPollInterval.value) {
        clearInterval(renewPollInterval.value)
        renewPollInterval.value = null
      }

      ElMessage.success('支付成功！刷新列表中...')
      setTimeout(() => {
        fetchProducts()
      }, 1000)
    } else {
      ElMessage.info('支付未完成，请继续等待')
    }
  } catch (error) {
    console.error('检查支付状态失败:', error)
  }
}

// 脱敏激活码
const maskActivationCode = (code) => {
  if (!code) return '-'
  if (code.length <= 12) return code.substring(0, 4) + '****' + code.substring(code.length - 4)
  return code.substring(0, 8) + '****' + code.substring(code.length - 4)
}

// 复制激活码
const copyCode = (code) => {
  if (!code) return
  navigator.clipboard.writeText(code).then(() => {
    ElMessage.success('激活码已复制')
  }).catch(() => {
    ElMessage.error('复制失败，请手动复制')
  })
}

// 获取剩余天数（用于判断临期）
const getDaysLeft = (expireTime) => {
  if (!expireTime) return 999
  const diff = new Date(expireTime).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

// 显示激活码明细
const showActivationDetails = async (plugin) => {
  currentPluginForDetails.value = plugin
  activationDialogVisible.value = true
  detailsCurrentPage.value = 1   // 重置页码
  await loadActivationDetails(plugin)
}

const handleDetailsPageChange = (page) => {
  detailsCurrentPage.value = page
  // 重新请求，使用当前插件
  if (currentPluginForDetails.value) {
    // 直接调用 showActivationDetails 会重置页码为1，所以我们需要单独写一个加载函数
    loadActivationDetails(currentPluginForDetails.value)
  }
}

// 新增仅加载数据（不重置页码）的方法
const loadActivationDetails = async (plugin) => {
  detailsLoading.value = true
  try {
    const response = await request.get('/public-plugin/activation-details', {
      params: {
        pluginKey: plugin.productCode,
        page: detailsCurrentPage.value,
        size: detailsPageSize.value
      }
    })
    if (response.code === '200') {
      activationDetails.value = response.data.list || []
      detailsTotal.value = response.data.total || 0
    } else {
      ElMessage.error(response.message || '获取注册码明细失败')
    }
  } catch (error) {
    console.error('获取注册码明细失败:', error)
    ElMessage.error('获取注册码明细失败')
  } finally {
    detailsLoading.value = false
  }
}


onMounted(() => {
  fetchProducts()
})

// onBeforeUnmount(() => {
//   if (renewPollInterval.value) {
//     clearInterval(renewPollInterval.value)
//   }
// })

onBeforeUnmount(() => {
  if (renewPollInterval.value) {
    clearInterval(renewPollInterval.value)
  }

  if (qrCodeExpireCheckInterval.value) {
    clearInterval(qrCodeExpireCheckInterval.value)
  }
})
</script>

<style scoped>
.user-plugins-container {
  --orders-font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  padding: 10px 6px;
  font-family: var(--orders-font);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 700px;
  width: 100%;
  box-sizing: border-box;
}

/* ===== 搜索区（卡片化） ===== */
.search-bar {
  margin-bottom: 18px;
  padding: 4px 0;
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  transition: none;
  flex-shrink: 0;
}

.search-bar::before {
  display: none;
}

.search-bar:hover {
  box-shadow: none;
  transform: none;
}

/* 搜索输入框 */
:deep(.search-bar .el-input__wrapper) {
  border-radius: 8px;
  box-shadow: 0 0 0 1px #d1d5db inset;
  padding: 4px 12px;
  transition: all 0.2s ease;
}

:deep(.search-bar .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #9ca3af inset;
}

:deep(.search-bar .el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #3b82f6 inset, 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
}

:deep(.search-bar .el-input__inner) {
  font-family: var(--orders-font);
  color: #0f172a;
}

:deep(.search-area .el-input__prefix-inner .el-icon) {
  color: #6b7280;
}

/* 搜索按钮 */
:deep(.search-bar .el-button--primary) {
  background: #3b82f6;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);
  transition: all 0.25s ease;
}

:deep(.search-bar .el-button--primary:hover) {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.35);
  background: #2563eb;
}

:deep(.search-bar .el-button--primary:active) {
  transform: translateY(0);
}

/* ===== 表格（圆角容器 + 现代表头 + 行 hover） ===== */
.table-wrapper {
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  -webkit-overflow-scrolling: touch;
  position: relative;
}

/* 滚动条样式 */
.table-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.table-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}
.table-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

:deep(.product-table) {
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

:deep(.product-table.el-table--border .el-table__inner-wrapper::after),
:deep(.product-table .el-table__border-left-patch) {
  display: none;
}

:deep(.product-table.el-table--border) {
  border: none !important;
}

:deep(.product-table .el-table__header th) {
  font-weight: 600;
  color: #0f172a;
  background-color: #f8fafc !important;
  font-size: 13px;
  border-bottom: 1px solid #e5e7eb !important;
  border-right: none !important;
  padding: 12px 10px;
}

:deep(.product-table .el-table__body td) {
  text-align: center;
  border-bottom: 1px solid #f1f5f9 !important;
  border-right: none !important;
  padding: 12px 10px;
  font-size: 13px;
  color: #334155;
}

:deep(.product-table .el-table__body tr:hover > td) {
  background-color: rgba(59, 130, 246, 0.04) !important;
}

:deep(.product-table .el-table__body tr.el-table__row--striped td) {
  background-color: #fafbfc !important;
}

:deep(.product-table .el-table__body tr.el-table__row--striped:hover > td) {
  background-color: rgba(59, 130, 246, 0.04) !important;
}

:deep(.product-table .el-table__body tr:last-child td) {
  border-bottom: none !important;
}

/* 自定义空数据样式 */
:deep(.product-table .el-table__empty-block) {
  min-height: 180px;
}

:deep(.product-table .el-table__empty-text) {
  color: #94a3b8;
  font-size: 14px;
}

/* ===== 版本标签 ===== */
.version-tag {
  border-radius: 6px;
  font-weight: 600;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  border-color: #a5b4fc;
  color: #4338ca;
}

/* 描述列样式 */
.description-cell {
  font-size: 13px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  max-height: 3em;
}

/* ===== 到期时间现代色 ===== */
.expired { color: #ef4444; font-weight: 700; }
.expiring { color: #f59e0b; font-weight: 700; }
.normal { color: #22c55e; font-weight: 700; }

/* ===== 续费按钮（渐变） ===== */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px 0;
}

.action-btn {
  font-weight: 600;
  border-radius: 8px;
  border: none;
  color: #fff;
  transition: all 0.25s ease;
  padding: 6px 16px;
  font-size: 12px;
  height: 24px;
  line-height: 1;
  cursor: pointer;
}

.action-btn.expired-btn {
  background: linear-gradient(135deg, #f97316 0%, #fb923c 100%);
  box-shadow: 0 2px 8px rgba(249, 115, 22, 0.3);
}
.action-btn.expired-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(249, 115, 22, 0.4);
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
}

.action-btn.active-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}
.action-btn.active-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
}

.action-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none !important;
}


/* ===== 分页（胶囊样式） ===== */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next),
:deep(.el-pagination .el-pager li) {
  border-radius: 8px !important;
  min-width: 34px;
  height: 34px;
  line-height: 34px;
  margin: 0 3px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #475569;
  font-weight: 500;
  transition: all 0.2s ease;
}

:deep(.el-pagination .el-pager li:hover),
:deep(.el-pagination .btn-prev:hover),
:deep(.el-pagination .btn-next:hover) {
  color: #3b82f6;
  border-color: #3b82f6;
}

:deep(.el-pagination .el-pager li.is-active) {
  background: #3b82f6 !important;
  color: #ffffff !important;
  border-color: #3b82f6 !important;
  font-weight: 600;
}

/* ===== 遮罩背景虚化（弹窗渲染到 body 用 :global） ===== */
:global(.renew-dialog-overlay) {
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;
  background:
      radial-gradient(ellipse 800px 600px at 50% 0%, rgba(59, 130, 246, 0.10), transparent 60%),
      radial-gradient(ellipse 700px 500px at 50% 100%, rgba(249, 115, 22, 0.08), transparent 55%),
      rgba(15, 23, 42, 0.55) !important;
}

/* ---- 弹窗外层容器（圆角 20px + 顶部光晕） ---- */
:global(.renew-dialog) {
  border-radius: 20px !important;
  overflow: hidden;
  background:
      radial-gradient(ellipse 600px 280px at 50% 0%, rgba(59, 130, 246, 0.12) 0%, transparent 70%),
      radial-gradient(ellipse 400px 200px at 80% 100%, rgba(249, 115, 22, 0.08) 0%, transparent 60%),
      #ffffff;                    /* ← 保持白色基底，搭配柔和光晕 */
  box-shadow:
      0 24px 80px rgba(15, 23, 42, 0.12),
      0 8px 32px rgba(59, 130, 246, 0.06),
      0 0 0 1px rgba(255, 255, 255, 0.3) inset;
  border: none !important;
  max-height: 92vh !important;
  display: flex;
  flex-direction: column;
}

:global(.renew-dialog .el-dialog__header) {
  padding: 0;
  margin: 0;
  position: relative;
}

:global(.renew-dialog .el-dialog__body) {
  padding: 0;
  flex: 1;
  overflow-y: auto;          /* 内容过多时滚动 */
  overflow-x: hidden;
  max-height: calc(92vh - 40px); /* 减去头部高度（自定义关闭按钮区域） */
}

/* 隐藏 Element UI 默认关闭按钮（我们使用自定义按钮） */
:global(.renew-dialog .el-dialog__headerbtn) {
  display: none !important;
}

/* ---- 自定义头部容器（只放关闭按钮） ---- */
.custom-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 5px 8px 0;
  position: relative;
  z-index: 5;
}

/* ---- 现代圆形毛玻璃关闭按钮 ---- */
.custom-close-btn {
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  color: #94a3b8;
  cursor: pointer;
  margin-top: -10px;
  margin-right: -10px;
  padding: 0;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
}

.custom-close-btn:hover {
  background: red;
  color: #ffffff;
  transform: rotate(90deg) scale(1.06);
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.32);
  border-color: transparent;
}

.custom-close-btn:active {
  transform: rotate(0deg) scale(0.92);
}

/* ---- 弹窗内容区域 ---- */
.renew-dialog-content {
  padding: 0;
  background: transparent;
  border-radius: 0 0 20px 20px;
}

/* ---- 插件信息区域（柔和渐变卡片 + 蓝橙装饰条） ---- */
.plugin-info-section {
  background: transparent;
  padding: 22px 28px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.7);
  position: relative;
}

.plugin-info-section::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 8%;
  right: 8%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #3b82f6, #f97316, transparent);
  opacity: 0.55;
}

.plugin-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.plugin-info-header h4 {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b 0%, #3b82f6 60%, #f97316 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.3px;
}

.current-expire {
  font-size: 14px;
  color: #475569;
  font-weight: 500;
}

.current-expire span {
  font-weight: 700;
  margin-left: 6px;
}

.current-expire .expired { color: #ef4444; }
.current-expire .expiring { color: #f59e0b; }
.current-expire .normal { color: #22c55e; }

.plugin-description {
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding-right: 20px;
}

/* ---- 主体双栏布局 ---- */
.renew-main-content {
  display: flex;
  min-height: 460px;
  background: transparent;
  gap: 0;
}

/* ---- 左侧：周期选择（浅蓝灰底） ---- */
.period-selection-section {
  background: transparent;
  flex: 0 0 66.666%;      /* 固定占比 2/3 */
  box-sizing: border-box;  /* padding 计入宽度 */
  padding: 28px 32px 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  position: relative;
  padding-left: 14px;
}

.section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 20px;
  border-radius: 2px;
  background: linear-gradient(180deg, #3b82f6, #f97316);
}

.section-title .el-icon {
  color: #3b82f6;
  font-size: 22px;
}

/* ---- 周期卡片（三列，圆角 16px，hover lift） ---- */
.period-horizontal-container {
  display: flex;
  gap: 14px;
  margin-bottom: 28px;
}

.period-horizontal-option {
  flex: 1;
  position: relative;
  padding: 22px 12px 18px;
  background: #ffffff;
  border: 2px solid #eef2f6;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  text-align: center;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
  overflow: visible;
}

.period-horizontal-option:hover {
  border-color: rgba(59, 130, 246, 0.45);
  transform: translateY(-6px);
  box-shadow: 0 16px 36px rgba(59, 130, 246, 0.14);
}

/* 选中态：蓝→橙渐变描边 + 光晕 */
.period-horizontal-option.active {
  border-color: transparent;
  background:
      linear-gradient(#f0f4ff, #f0f4ff) padding-box,        /* 内区纯色浅蓝背景 */
      linear-gradient(135deg, #3b82f6 0%, #f97316 100%) border-box; /* 边框渐变 */
  border: 2px solid transparent;
  box-shadow:
      0 12px 32px rgba(59, 130, 246, 0.18),
      0 4px 16px rgba(249, 115, 22, 0.10);
  transform: translateY(-3px);
}

/* 热门/超值：与 active 配合时仍以蓝橙主色为主，但徽章颜色区分 */
.period-horizontal-option.popular {
  border-color: rgba(245, 158, 11, 0.45);
}
.period-horizontal-option.premium {
  border-color: rgba(34, 197, 94, 0.35);
}
.period-horizontal-option.popular.active,
.period-horizontal-option.premium.active {
  background:
      linear-gradient(#f0f4ff, #f0f4ff) padding-box,
      linear-gradient(135deg, #3b82f6 0%, #f97316 100%) border-box;
  border: 2px solid transparent;
}

/* 顶部角标（渐变胶囊） */
.period-top-badge {
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  color: #fff;
  padding: 3px 14px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 6px 14px rgba(59, 130, 246, 0.32);
  white-space: nowrap;
}

.period-horizontal-option.popular .period-top-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #fb923c 100%);
  box-shadow: 0 6px 14px rgba(245, 158, 11, 0.35);
}
.period-horizontal-option.premium .period-top-badge {
  background: linear-gradient(135deg, #22c55e 0%, #4ade80 100%);
  box-shadow: 0 6px 14px rgba(34, 197, 94, 0.30);
}

/* 周期内容 */
.period-main-content .period-label {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.period-main-content .period-price {
  margin-bottom: 4px;
}
.period-main-content .period-price .currency {
  font-size: 16px;
  font-weight: 600;
  color: #f97316;
  vertical-align: top;
  display: inline-block;
  transform: translateY(2px);
}
.period-main-content .period-price .amount {
  font-size: 30px;
  font-weight: 800;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-left: 1px;
  letter-spacing: -1px;
}

.period-main-content .period-days {
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
}
.period-main-content .period-desc {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

/* 选中对勾（蓝橙渐变圆形） */
.period-check-mark {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 30px;
  height: 30px;
  background: #0ab80a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.36);
  border: 2px solid #ffffff;
}
.period-check-mark .el-icon {
  color: #fff;
  font-size: 16px;
  font-weight: bold;
}

/* ---- 续费说明（柔和渐变卡片） ---- */
.renew-tips {
  margin-top: 28px;
  padding: 20px 22px;
  background:
      radial-gradient(ellipse 300px 150px at 100% 0%, rgba(249, 115, 22, 0.08), transparent 70%),
      linear-gradient(135deg, #f8fafc 0%, #fff3e8 100%);
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.7);
  position: relative;
}

.tips-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 10px;
}
.tips-header .el-icon {
  color: #3b82f6;
  font-size: 16px;
}

.tips-content p {
  margin: 5px 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.65;
  padding-left: 6px;
}
.tips-content p::before {
  content: '• ';
  color: #f97316;
  font-weight: 800;
}

/* ---- 右侧：支付区域 ---- */
.payment-section {
  flex: 0 0 33.333%;      /* 固定占比 1/3 */
  box-sizing: border-box;
  padding: 28px 13px 20px;
  display: flex;
  flex-direction: column;
  background: transparent !important;
}

/* 支付金额（蓝橙融合渐变卡片） */
.payment-amount {
  background:
      radial-gradient(ellipse 200px 100px at 0% 0%, rgba(59, 130, 246, 0.18), transparent 70%),
      radial-gradient(ellipse 200px 100px at 100% 100%, rgba(249, 115, 22, 0.20), transparent 70%),
      linear-gradient(135deg, #eff6ff 0%, #fff3e8 100%);
  padding: 22px 16px;
  border-radius: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;           /* ← 新增，禁止换行 */
  gap: 10px;                   /* ← 新增，保证间距 */
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-bottom: 12px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  min-width: 0;
}

.payment-amount::after {
  content: '';
  position: absolute;
  bottom: 0; left: 10%; right: 10%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #3b82f6, #f97316, transparent);
  opacity: 0.55;
}

.amount-label {
  font-size: 15px;
  color: #1e293b;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.amount-value {
  font-size: 38px;
  font-weight: 800;
  background: linear-gradient(135deg, #3b82f6 0%, #f97316 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -1.5px;
  line-height: 1.1;
  white-space: nowrap;
  flex-shrink: 1;
  text-align: right;       /* 新增：右对齐，保持位置稳定 */
}

/* ---- 二维码区域 ---- */
.qrcode-container {
  background: transparent;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;   /* 不再整体居中，改为顶部对齐 */
  padding: 10px;
}

.qrcode-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 16px;           /* 与wrapper间距不变，固定不动 */
}
.qrcode-header .el-icon {
  color: #07c160;
}

/* 二维码包装器（白色卡片 + 蓝色光晕 + 圆角 20px） */
.qrcode-wrapper {
  position: relative;
  width: 100%;
  max-width: 228px;
  aspect-ratio: 1 / 1;     /* 保持正方形 */
  display: flex;             /* 改为 flex，让内容居中 */
  align-items: center;
  justify-content: center;
  background: transparent;
  padding: 14px;
  border-radius: 20px;
  box-sizing: border-box;
  box-shadow:
      0 12px 36px rgba(15, 23, 42, 0.08),
      0 4px 12px rgba(59, 130, 246, 0.10),
      0 0 0 1px rgba(226, 232, 240, 0.7);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.qrcode-wrapper:hover {
  box-shadow:
      0 18px 48px rgba(15, 23, 42, 0.12),
      0 8px 24px rgba(59, 130, 246, 0.18);
  transform: translateY(-2px);
}

.qrcode-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  flex-shrink: 0;
  align-self: center;  /* 确保垂直居中 */
  border-radius: 12px;
}

/* 二维码状态遮罩（模糊 + 渐变） */
.success-overlay,
.expired-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border-radius: 20px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  animation: qrcodeFadeIn 0.4s ease;
}

/* 内容容器 - 纵向居中 */
.expired-content,
.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* 文字样式保持不变，可调整间距 */
.expired-text {
  font-size: 18px;
  font-weight: 700;
  color: #ef4444;
  margin: 8px 0 0;             /* 与图标保持间距 */
}

.success-overlay {
  box-shadow: inset 0 0 0 2px rgba(34, 197, 94, 0.3);
}

.success-overlay .success-text {
  font-size: 22px;
  font-weight: 800;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 8px 0 0;
}
.success-overlay .success-hint {
  font-size: 14px;
  color: #475569;
}

.expired-overlay {
  cursor: pointer;
  background: radial-gradient(ellipse at center, rgba(255, 245, 245, 0.85), rgba(255, 255, 255, 0.95));
  box-shadow: inset 0 0 0 2px rgba(239, 68, 68, 0.25);
}
.expired-overlay:hover {
  background: radial-gradient(ellipse at center, rgba(255, 235, 235, 0.92), rgba(255, 255, 255, 0.98));
}
.expired-overlay .expired-text {
  font-size: 18px;
  font-weight: 700;
  color: #ef4444;
  margin: 8px 0 0;
}

/* 加载状态（备用） */
.qrcode-loading-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: #f8fafc;
  color: #94a3b8;
  gap: 10px;
  border-radius: 12px;
}
.qrcode-loading-placeholder .loading-icon {
  font-size: 36px;
  color: #3b82f6;
  animation: spin 1.2s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ---- 支付状态提示 ---- */
.payment-status-hint {
  margin-top: 16px;
  padding: 12px 18px;
  border-radius: 12px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  width: 100%;
}
.status-pending {
  color: #f59e0b;
  background: #fffbeb;
  border: 1px solid #fde68a;
}
.status-success {
  color: #16a34a;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.12);
}
.status-pending .el-icon,
.status-success .el-icon {
  margin-right: 6px;
  vertical-align: middle;
}

/* ---- 支付操作按钮（保留但隐藏，如需展示可启用） ---- */
.payment-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 18px;
}
.payment-actions .el-button {
  min-width: 140px;
  height: 40px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 12px;
  transition: all 0.25s ease;
}

/* ---- 通用动画 ---- */
@keyframes qrcodeFadeIn {
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
}

/* ===== 激活码明细弹窗样式 ===== */
:global(.registration-code-history-overlay) {
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  background: rgba(15, 23, 42, 0.5) !important;
}

:global(.registration-code-history-dialog) {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow:
      0 24px 80px rgba(15, 23, 42, 0.15),
      0 0 0 1px rgba(255, 255, 255, 0.2) inset;
  border: none !important;
}

:global(.registration-code-history-dialog.el-dialog .el-dialog__header) {
  padding: 12px 28px 12px 10px !important;
}

/* 隐藏默认关闭按钮 */
:global(.registration-code-history-dialog .el-dialog__headerbtn) {
  display: none !important;
}

.registration-code-history-custom-close-btn {
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  color: #94a3b8;
  cursor: pointer;
  margin-top: -30px;
  margin-right: -30px;
  padding: 0;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
}

.registration-code-history-custom-close-btn:hover {
  background: red;
  color: #ffffff;
  transform: rotate(90deg) scale(1.06);
  box-shadow: 0 6px 18px rgba(59, 130, 246, 0.32);
  border-color: transparent;
}

.registration-code-history-custom-close-btn:active {
  transform: rotate(0deg) scale(0.92);
}

/* 自定义头部容器 */
.registration-code-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  background: #000;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 表格包装器（圆角 + 边框） */
.registration-code-history-table-wrapper {
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

:deep(.registration-code-history-table) {
  border: none !important;
  box-shadow: none !important;
}
:deep(.registration-code-history-table.el-table--border .el-table__inner-wrapper::after),
:deep(.registration-code-history-table .el-table__border-left-patch) {
  display: none;
}
:deep(.registration-code-history-table .el-table__header th) {
  background-color: #f8fafc !important;
  color: #0f172a;
  font-weight: 600;
  font-size: 13px;
  border-bottom: 1px solid #e5e7eb !important;
  padding: 12px 8px;
}
:deep(.registration-code-history-table .el-table__body td) {
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 12px 8px;
  font-size: 13px;
  color: #334155;
}
:deep(.registration-code-history-table .el-table__body tr:hover > td) {
  background-color: rgba(59, 130, 246, 0.04) !important;
}
:deep(.registration-code-history-table .el-table__body tr:last-child td) {
  border-bottom: none !important;
}

/* 等宽字体（用于注册码和激活码） */
.mono-text {
  font-family: 'JetBrains Mono', 'Cascadia Code', 'Consolas', monospace;
  font-size: 13px;
  letter-spacing: 0.3px;
  word-break: break-all;
  display: inline-block;
  max-width: 100%;
}

/* 到期时间颜色（复用） */
.expired {
  color: #ef4444;
  font-weight: 700;
}
.normal {
  color: #22c55e;
  font-weight: 700;
}

/* ---- 响应式适配 ---- */
@media (max-width: 768px) {
  .user-plugins-container {
    padding: 16px;
    border-radius: 14px;
  }
  .search-bar {
    padding: 14px 16px;
  }
  .renew-main-content {
    flex-direction: column;
    min-height: auto;
  }
  .period-selection-section {
    flex: 1 1 auto;
    width: 100%;
    padding: 20px 16px;
  }
  .period-horizontal-container {
    flex-direction: column;
    gap: 14px;
  }
  .period-horizontal-option {
    min-height: 100px;
    padding: 16px 12px;
  }
  .payment-section {
    padding: 20px;
  }
  .qrcode-image {
    width: 160px;
    height: 160px;
  }
  :global(.renew-dialog) {
    width: 94vw !important;
    max-width: 440px;
    max-height: 90vh !important;
  }
  :global(.renew-dialog .el-dialog__body) {
    max-height: calc(90vh - 44px); /* 调整差值 */
  }

  .custom-close-btn {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }
  .plugin-info-header h4 { font-size: 18px; }
  .amount-value { font-size: 30px; }

  .renew-main-content {
    flex-direction: column;
    min-height: auto;
  }
  .period-selection-section,
  .payment-section {
    flex: 1 1 auto;      /* 恢复为自适应宽度 */
    box-sizing: border-box;
    /* 可根据需要调整 padding */
  }

  :global(.activation-dialog) {
    width: 94vw !important;
    max-width: 440px;
  }
  .activation-dialog-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  .dialog-title {
    font-size: 16px;
  }
  .header-actions .el-tag {
    font-size: 10px;
    padding: 0 6px;
    height: 20px;
    line-height: 18px;
  }
  .custom-close-btn {
    width: 24px;
    height: 24px;
    font-size: 14px;
  }
  :deep(.activation-table .el-table__header th),
  :deep(.activation-table .el-table__body td) {
    padding: 8px 4px;
    font-size: 12px;
  }
  .mono-text {
    font-size: 11px;
  }
  :deep(.activation-table .el-button--small) {
    font-size: 10px;
    padding: 0 4px;
  }
}
</style>
